@if (menuMode() === 'fixed') {
  <div class="toolbar border-b pb-2 mb-2 flex flex-wrap gap-1">
    <ng-container *ngTemplateOutlet="menu" />
  </div>
}

<div
  class="w-full [&_.ProseMirror]:min-h-[150px] [&_.ProseMirror]:p-3 [&_.ProseMirror]:border [&_.ProseMirror]:border-gray-300 [&_.ProseMirror]:rounded-md [&_.ProseMirror]:bg-white [&_.ProseMirror]:outline-none [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:max-h-[500px] [&_.ProseMirror:focus]:border-blue-500 [&_.ProseMirror:focus]:ring-2 [&_.ProseMirror:focus]:ring-blue-500/20 [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-bold"
  tiptapEditor
  [editor]="editor"
  [formControl]="richTextControl"
  [ngClass]="{
    'tip-tap-error': richTextControl.invalid && (richTextControl.dirty || richTextControl.touched),
  }"
></div>
@if (!(maxCharacterCount() | isNil)) {
  <div class="text-xs text-gray-500 text-right">
    {{ characterCount() }} / {{ maxCharacterCount() }}
  </div>
}

<mat-error class="mat-error-without-form-field" [control]="richTextControl" />

@if (menuMode() === 'bubble') {
  <tiptap-bubble-menu [editor]="editor">
    <ng-container *ngTemplateOutlet="menu" />
  </tiptap-bubble-menu>
}

@if (menuMode() === 'floating') {
  <tiptap-floating-menu [editor]="editor">
    <div class="floating-menu-container bg-white shadow-lg rounded-lg border p-1 flex gap-1">
      <ng-container *ngTemplateOutlet="menu" />
    </div>
  </tiptap-floating-menu>
}

<ng-template #menu>
  <ng-container *transloco="let t; prefix: i18nScope">
    @if (hasBold()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('bold')"
        [matTooltip]="t('RICH_TEXT_EDITOR.BOLD')"
        (click)="editor.commands.toggleBold()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-bold" />
      </button>
    }

    @if (hasItalic()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('italic')"
        [matTooltip]="t('RICH_TEXT_EDITOR.ITALIC')"
        (click)="editor.commands.toggleItalic()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-italic" />
      </button>
    }

    @if (hasUnderline()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('underline')"
        [matTooltip]="t('RICH_TEXT_EDITOR.UNDERLINE')"
        (click)="editor.commands.toggleUnderline()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-underline" />
      </button>
    }

    @if (hasStrike()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('strike')"
        [matTooltip]="t('RICH_TEXT_EDITOR.STRIKE')"
        (click)="editor.commands.toggleStrike()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-strikethrough" />
      </button>
    }

    @if (hasCode()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('code')"
        [matTooltip]="t('RICH_TEXT_EDITOR.CODE')"
        (click)="editor.commands.toggleCode()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-code-tags" />
      </button>
    }

    @if (hasBlockquote()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('blockquote')"
        [matTooltip]="t('RICH_TEXT_EDITOR.BLOCKQUOTE')"
        (click)="editor.commands.toggleBlockquote()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-quote-close" />
      </button>
    }

    @if (hasBulletList()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('bulletList')"
        [matTooltip]="t('RICH_TEXT_EDITOR.BULLET_LIST')"
        (click)="editor.commands.toggleBulletList()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-list-bulleted" />
      </button>
    }

    @if (hasOrderedList()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('orderedList')"
        [matTooltip]="t('RICH_TEXT_EDITOR.ORDERED_LIST')"
        (click)="editor.commands.toggleOrderedList()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-list-numbered" />
      </button>
    }

    @if (hasHeading()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('heading')"
        [matMenuTriggerFor]="menu"
        [matTooltip]="t('RICH_TEXT_EDITOR.HEADING_TOOL')"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-size" />
      </button>

      <mat-menu #menu="matMenu">
        @for (level of levels; track level) {
          <button
            mat-menu-item
            type="button"
            [class.active]="editor.isActive('heading', { level: level })"
            (click)="editor.commands.toggleHeading({ level: level })"
          >
            {{ t(`RICH_TEXT_EDITOR.HEADING.H${level}`) }}
          </button>
        }
      </mat-menu>
    }

    @if (hasLink()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive('link')"
        [matTooltip]="t('RICH_TEXT_EDITOR.LINK')"
        (click)="setLink()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-link" />
      </button>

      @if (editor.isActive('link')) {
        <button
          class="active"
          mat-icon-button
          type="button"
          [matTooltip]="t('RICH_TEXT_EDITOR.UNSET_LINK')"
          (click)="unsetLink()"
        >
          <mat-icon customMatSvgIcon="pct-mdi-link-off" />
        </button>
      }
    }

    @if (hasTextStyle()) {
      <div class="relative inline-block">
        <input
          #textColorPicker
          class="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
          type="color"
          [matTooltip]="t('RICH_TEXT_EDITOR.TEXT_COLOR')"
          [value]="editor.getAttributes('textStyle')['color'] || '#000'"
          (input)="editor.chain().focus().setColor(textColorPicker.value).run()"
        />
        <button
          mat-icon-button
          type="button"
          [class.active]="editor.getAttributes('textStyle')['color']"
          [matTooltip]="t('RICH_TEXT_EDITOR.TEXT_COLOR')"
        >
          <mat-icon
            customMatSvgIcon="pct-mdi-format-color-text"
            [style.color]="editor.getAttributes('textStyle')['color'] || 'currentColor'"
          />
        </button>
      </div>

      <div class="relative inline-block">
        <input
          #textBackgroundColorPicker
          class="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
          type="color"
          [matTooltip]="t('RICH_TEXT_EDITOR.TEXT_BACKGROUND_COLOR')"
          [value]="editor.getAttributes('textStyle')['backgroundColor'] || '#fff'"
          (input)="editor.chain().focus().setBackgroundColor(textBackgroundColorPicker.value).run()"
        />
        <button
          mat-icon-button
          type="button"
          [class.active]="editor.getAttributes('textStyle')['backgroundColor']"
          [matTooltip]="t('RICH_TEXT_EDITOR.TEXT_BACKGROUND_COLOR')"
        >
          <mat-icon
            customMatSvgIcon="pct-mdi-format-color-fill"
            [style.color]="editor.getAttributes('textStyle')['backgroundColor'] || 'currentColor'"
          />
        </button>
      </div>
    }

    @if (hasTextAlign()) {
      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive({ textAlign: 'left' })"
        [matTooltip]="t('RICH_TEXT_EDITOR.LEFT')"
        (click)="editor.commands.setTextAlign('left')"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-align-left" />
      </button>

      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive({ textAlign: 'center' })"
        [matTooltip]="t('RICH_TEXT_EDITOR.CENTER')"
        (click)="editor.commands.setTextAlign('center')"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-align-center" />
      </button>

      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive({ textAlign: 'right' })"
        [matTooltip]="t('RICH_TEXT_EDITOR.RIGHT')"
        (click)="editor.commands.setTextAlign('right')"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-align-right" />
      </button>

      <button
        mat-icon-button
        type="button"
        [class.active]="editor.isActive({ textAlign: 'justify' })"
        [matTooltip]="t('RICH_TEXT_EDITOR.JUSTIFY')"
        (click)="editor.commands.setTextAlign('justify')"
      >
        <mat-icon customMatSvgIcon="pct-mdi-format-align-justify" />
      </button>
    }

    @if (hasYoutube()) {
      <button
        mat-icon-button
        type="button"
        [matTooltip]="t('RICH_TEXT_EDITOR.YOUTUBE')"
        (click)="insertYoutube()"
      >
        <mat-icon customMatSvgIcon="pct-mdi-youtube" />
      </button>
    }

    <button
      mat-icon-button
      type="button"
      [matTooltip]="t('RICH_TEXT_EDITOR.UNDO')"
      (click)="editor.commands.undo()"
    >
      <mat-icon customMatSvgIcon="pct-mdi-undo" />
    </button>
    <button
      mat-icon-button
      type="button"
      [matTooltip]="t('RICH_TEXT_EDITOR.REDO')"
      (click)="editor.commands.redo()"
    >
      <mat-icon customMatSvgIcon="pct-mdi-redo" />
    </button>
  </ng-container>
</ng-template>
